"use client";

import React, {
  createContext,
  useContext,
  useCallback,
  useState,
  useEffect,
  useMemo,
} from "react";
import { auth, isFirebaseInitialized, checkIsAdmin } from "@/lib/firebase";
import { User } from "firebase/auth";

export interface AdminContextType {
  isAdmin: boolean;
  isLoading: boolean;
  checkAdminStatus: () => Promise<void>;
  isAdminViewEnabled: boolean;
  toggleAdminView: () => void;
}

const AdminContext = createContext<AdminContextType>({
  isAdmin: false,
  isLoading: true,
  checkAdminStatus: async () => {},
  isAdminViewEnabled: false,
  toggleAdminView: () => {},
});

const ADMIN_VIEW_STORAGE_KEY = "aseed_admin_view_enabled";

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdminViewEnabled, setIsAdminViewEnabled] = useState(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(ADMIN_VIEW_STORAGE_KEY);
      return stored === "true";
    }
    return false;
  });

  const checkAdminStatus = useCallback(async () => {
    try {
      if (!isFirebaseInitialized() || !auth) {
        setIsAdmin(false);
        return;
      }
      const user = auth.currentUser;
      const adminStatus = await checkIsAdmin(user);
      setIsAdmin(adminStatus);
    } catch (error) {
      console.error("Error checking admin status:", error);
      setIsAdmin(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check admin status when component mounts and when auth state changes
  useEffect(() => {
    if (!auth) {
      setIsAdmin(false);
      setIsLoading(false);
      return;
    }
    const unsubscribe = auth.onAuthStateChanged(async (user: User | null) => {
      if (user) {
        await checkAdminStatus();
      } else {
        setIsAdmin(false);
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, [checkAdminStatus]);

  const toggleAdminView = useCallback(() => {
    setIsAdminViewEnabled((prev) => {
      const newValue = !prev;
      if (typeof window !== "undefined") {
        localStorage.setItem(ADMIN_VIEW_STORAGE_KEY, String(newValue));
      }
      return newValue;
    });
  }, []);

  const value = useMemo(
    () => ({
      isAdmin,
      isLoading,
      checkAdminStatus,
      isAdminViewEnabled,
      toggleAdminView,
    }),
    [isAdmin, isLoading, checkAdminStatus, isAdminViewEnabled, toggleAdminView]
  );

  return (
    <AdminContext.Provider value={value}>{children}</AdminContext.Provider>
  );
};

export const useAdmin = (): AdminContextType => {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error("useAdmin must be used within an AdminProvider");
  }
  return context;
};
